import React from "react";

// Shared presentation for the financial list pages (Charging Sessions, Wallets,
// Wallet Transactions) so they use one typography scale, one table style and
// one spacing system. Purely visual: no data handling lives here.

// page container: the dashboard <main> already provides the outer padding
export const PageShell = ({ children }) => <div className="mx-auto max-w-[1600px] space-y-5 pb-10">{children}</div>;

// 26px title, 13px muted description, optional actions on the right
export const PageHeader = ({ title, description, icon: Icon, actions }) => (
  <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
    <div className="min-w-0">
      <h1 className="flex items-center gap-2.5 text-[26px] font-bold leading-8 tracking-tight text-brand-950">
        {Icon && (
          <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-brand-50 text-brand-600 ring-1 ring-inset ring-brand-100">
            <Icon className="h-4 w-4" />
          </span>
        )}
        {title}
      </h1>
      {description && <p className="mt-1 text-13 text-neutral-500">{description}</p>}
    </div>
    {actions && <div className="flex flex-shrink-0 flex-wrap items-center gap-2">{actions}</div>}
  </div>
);

// one white bar holding the page's filters
export const Toolbar = ({ children, className = "" }) => (
  <div className={`rounded-xl border border-neutral-200 bg-white p-4 shadow-card ${className}`}>{children}</div>
);

// label above a filter control
export const Field = ({ label, children, className = "" }) => (
  <label className={`flex min-w-0 flex-col gap-1.5 ${className}`}>
    <span className="text-xs font-medium text-neutral-600">{label}</span>
    {children}
  </label>
);

// card around a table, with an optional header strip (count, actions)
export const TableCard = ({ header, footer, children }) => (
  <div className="overflow-hidden rounded-xl border border-neutral-200 bg-white shadow-card">
    {header && (
      <div className="flex min-h-[44px] flex-wrap items-center justify-between gap-2 border-b border-neutral-200 px-4 py-2.5 text-13 text-neutral-500">
        {header}
      </div>
    )}
    <div className="overflow-x-auto">{children}</div>
    {footer && <div className="border-t border-neutral-200 px-4 py-3 text-center">{footer}</div>}
  </div>
);

// table pieces: 40px header row, 52px body rows
export const tableClass = "min-w-full text-13";
export const theadClass = "border-b border-neutral-200 bg-neutral-50";
export const tbodyClass = "divide-y divide-neutral-100";
export const rowClass = "transition-colors even:bg-neutral-50/60 hover:!bg-brand-50/50";

export const Th = ({ children, align = "left", className = "", ...rest }) => (
  <th
    className={`h-10 whitespace-nowrap px-4 text-[11px] font-semibold uppercase tracking-[0.06em] text-neutral-500 ${
      align === "right" ? "text-right" : "text-left"
    } ${className}`}
    {...rest}
  >
    {children}
  </th>
);

export const Td = ({ children, align = "left", className = "", ...rest }) => (
  <td className={`h-[52px] whitespace-nowrap px-4 py-2 align-middle text-neutral-700 ${align === "right" ? "text-right" : "text-left"} ${className}`} {...rest}>
    {children}
  </td>
);

// a monetary value: 14px semibold, tabular figures
export const Money = ({ children }) => <span className="text-sm font-semibold tabular-nums text-brand-950">{children}</span>;

// an identifier: monospace, truncated with the full value on hover
export const Mono = ({ children, max = "max-w-[140px] sm:max-w-[220px]" }) => (
  <span className={`block truncate font-mono text-[12.5px] text-neutral-700 ${max}`} title={typeof children === "string" ? children : undefined}>
    {children}
  </span>
);

// date on the first line, time underneath in muted text
export const DateTime = ({ date, time }) => (
  <div className="leading-tight">
    <div className="font-medium tabular-nums text-neutral-800">{date}</div>
    {time && <div className="mt-0.5 text-xs tabular-nums text-neutral-500">{time}</div>}
  </div>
);

// full-width message row inside a table body
export const MessageRow = ({ colSpan, children, tone = "muted" }) => (
  <tr>
    <td colSpan={colSpan} className={`px-4 py-12 text-center text-13 ${tone === "error" ? "text-red-600" : "text-neutral-500"}`}>
      {children}
    </td>
  </tr>
);

// subtle status badges
export const BADGE_TONES = {
  green: "bg-green-50 text-green-700 ring-green-600/15",
  amber: "bg-amber-50 text-amber-700 ring-amber-600/20",
  brand: "bg-brand-50 text-brand-700 ring-brand-600/15",
  red: "bg-red-50 text-red-700 ring-red-600/15",
  neutral: "bg-neutral-50 text-neutral-600 ring-neutral-500/20",
};

export const StatusBadge = ({ tone = "neutral", pulse = false, children, ...rest }) => (
  <span className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${BADGE_TONES[tone]}`} {...rest}>
    <span className="relative flex h-1.5 w-1.5">
      {pulse && <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-current opacity-50" />}
      <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-current" />
    </span>
    {children}
  </span>
);
